import React, { useState } from "react";
import { FlexCol, FlexRow } from "./generic/Containers";
import { BCIcon, ICONS } from "./generic/BCIcons";
import {BCHeader1, BCHeader2, BCHeader3, Body1, Body2, Subtitle1} from "./generic/BCHeaders";
import { BCPrimaryButton, BCSecondaryButton, BCTertiaryButton } from "./generic/BCButtons";
import { sendEmail as sendEmailAPI } from "../util/APIHelper";
import "../css/GenericComponents.css";

interface EducatorInquiryModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface EducatorFormData {
    name: string;
    school: string;
    studentCount: string;
    email: string;
    emailInquiry?: string;
}

interface FormErrors {
    name?: string;
    school?: string;
    studentCount?: string;
    email?: string;
    emailInquiry?: string;
}

export const EducatorInquiryModal: React.FC<EducatorInquiryModalProps> = ({
    isOpen,
    onClose
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState<EducatorFormData>({
        name: "", school: "", studentCount: "", email: "", emailInquiry: ""
    });
    const [selectedAction, setSelectedAction] = useState<"call" | "email" | null>(null);
    const [errors, setErrors] = useState<FormErrors>({});
    const [showErrors, setShowErrors] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    React.useEffect(() => {
        if (isOpen) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }

        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleInputChange = (field: keyof EducatorFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const validateStep = (step: number): boolean => {
        const newErrors: FormErrors = {};

        if (step === 1) {
            if (!formData.name.trim()) newErrors.name = "Name is required";
            if (!formData.school.trim()) newErrors.school = "School/Organization is required";
            if (!formData.studentCount) newErrors.studentCount = "Please select student count";
            if (!formData.email.trim()) newErrors.email = "Email is required";
        }
        
        if (step === 3 && selectedAction === 'email') {
            if (!formData.emailInquiry?.trim()) newErrors.emailInquiry = "Please provide details about your inquiry";
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const sendEmail = async (actionType: 'email' | 'call'): Promise<boolean> => {
        try {
            const isEmailInquiry = actionType === 'email';
            const subject = isEmailInquiry 
                ? `Educator Inquiry from ${formData.name} - ${formData.school}`
                : `Educator Call Scheduled from ${formData.name} - ${formData.school}`;
            
            const messageContent = isEmailInquiry 
                ? formData.emailInquiry || ''
                : 'This educator has scheduled a call to discuss Beyond Code for their students.';

            const emailData = {
                to: 'dthurman@beyondcode.app',
                subject: subject,
                html: `
                    <h2>New Educator ${isEmailInquiry ? 'Inquiry' : 'Call Scheduled'}</h2>
                    <p><strong>Name:</strong> ${formData.name}</p>
                    <p><strong>School/Organization:</strong> ${formData.school}</p>
                    <p><strong>Number of Students:</strong> ${formData.studentCount}</p>
                    <p><strong>Email:</strong> ${formData.email}</p>
                    <p><strong>Action:</strong> ${isEmailInquiry ? 'Email Inquiry' : 'Call Scheduled'}</p>
                    ${isEmailInquiry ? `<p><strong>Message:</strong></p><p>${messageContent}</p>` : ''}
                `,
                text: `
                    New Educator ${isEmailInquiry ? 'Inquiry' : 'Call Scheduled'}
                    
                    Name: ${formData.name}
                    School/Organization: ${formData.school}
                    Number of Students: ${formData.studentCount}
                    Email: ${formData.email}
                    Action: ${isEmailInquiry ? 'Email Inquiry' : 'Call Scheduled'}
                    ${isEmailInquiry ? `\nMessage:\n${messageContent}` : ''}
                `
            };

            // Use the APIHelper to send the email
            const result = await sendEmailAPI(
                emailData.to,
                emailData.subject,
                emailData.html,
                emailData.text
            );
            return result && result.success;
        } catch (error) {
            console.error('Error sending email:', error);
            return false;
        }
    };

    const handleSubmit = async () => {
        if (selectedAction === 'email') {
            const isValid = validateStep(3);
            if (!isValid) {
                setShowErrors(true);
                return;
            }

            setIsSubmitting(true);
            setSubmitError(null);

            const emailSent = await sendEmail('email');
            
            if (emailSent) {
                setCurrentStep(4);
            } else {
                setSubmitError('Failed to send email. Please try again or contact us directly.');
            }
            
            setIsSubmitting(false);
        }
    };

    const handleNext = () => {
        const isValid = validateStep(1);
        if (isValid) {
            setCurrentStep(2);
            setShowErrors(false);
        } else {
            setShowErrors(true);
        }
    };

    return (
        <div className="overlay-container educator-overlay" onClick={onClose}>
            <FlexCol 
                className="overlay-content educator-inquiry-modal" 
                onClick={(e) => e.stopPropagation()}
                maxWidth={32}
                widthPercent={90}
                gap={1.5}
                padding={1.5}
                style={{ maxHeight: '90vh', height: 'fit-content', overflowY: 'auto' }}
            >
                <FlexRow justify="space-between" align="center" widthPercent={100}>
                    <BCHeader1 text="Get Beyond Code for Your Students" style={{fontSize: '1.2rem'}}/>
                    <BCIcon 
                        icon={ICONS.CLOSE} 
                        onClick={onClose}
                        className="educator-inquiry-close-button"
                        style={{ cursor: 'pointer', fontSize: '1.5em' }}
                    />
                </FlexRow>

                {currentStep === 1 && (
                    <FlexCol gap={1.5} widthPercent={100}>
                        <FlexRow justify="space-between" align="center" widthPercent={100}>
                            <Subtitle1 text="Tell us about your organization" />
                            <FlexRow gap={1} align="center">
                                <div 
                                    className={`progress-step ${currentStep >= 1 ? 'active' : ''}`}
                                    style={{
                                        width: '1.8em',
                                        height: '1.8em',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: currentStep >= 1 ? 'var(--primary-blue)' : 'var(--border-default)',
                                        color: currentStep >= 1 ? 'white' : 'var(--secondary-text-color)',
                                        fontWeight: 'bold',
                                        fontSize: '0.8em'
                                    }}
                                >
                                    1
                                </div>
                                <div 
                                    className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}
                                    style={{
                                        width: '1.8em',
                                        height: '1.8em',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: currentStep >= 2 ? 'var(--primary-blue)' : 'var(--border-default)',
                                        color: currentStep >= 2 ? 'white' : 'var(--secondary-text-color)',
                                        fontWeight: 'bold',
                                        fontSize: '0.8em'
                                    }}
                                >
                                    2
                                </div>
                                <div 
                                    className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}
                                    style={{
                                        width: '1.8em',
                                        height: '1.8em',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: currentStep >= 3 ? 'var(--primary-blue)' : 'var(--border-default)',
                                        color: currentStep >= 3 ? 'white' : 'var(--secondary-text-color)',
                                        fontWeight: 'bold',
                                        fontSize: '0.8em'
                                    }}
                                >
                                    3
                                </div>
                            </FlexRow>
                        </FlexRow>
                        
                        <FlexCol gap={1}>
                            <FlexCol gap={0.5}>
                                <Body2 text="Name *" />
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    placeholder="Your full name"
                                    className={`educator-form-input ${showErrors && errors.name ? 'error' : ''}`}
                                />
                                {showErrors && errors.name && (
                                    <Body2 text={errors.name} style={{ color: 'var(--error-color, #ef4444)', fontSize: '0.8em' }} />
                                )}
                            </FlexCol>

                            <FlexCol gap={0.5}>
                                <Body2 text="School / Organization *" />
                                <input
                                    type="text"
                                    value={formData.school}
                                    onChange={(e) => handleInputChange('school', e.target.value)}
                                    placeholder="School or organization name"
                                    className={`educator-form-input ${showErrors && errors.school ? 'error' : ''}`}
                                />
                                {showErrors && errors.school && (
                                    <Body2 text={errors.school} style={{ color: 'var(--error-color, #ef4444)', fontSize: '0.8em' }} />
                                )}
                            </FlexCol>

                            <FlexCol gap={0.5}>
                                <Body2 text="Number of Students *" />
                                <select
                                    value={formData.studentCount}
                                    onChange={(e) => handleInputChange('studentCount', e.target.value)}
                                    className={`educator-form-input ${showErrors && errors.studentCount ? 'error' : ''}`}
                                >
                                    <option value="">Select student count</option>
                                    <option value="1-20">1–20</option>
                                    <option value="20-100">20–100</option>
                                    <option value="100+">100+</option>
                                </select>
                                {showErrors && errors.studentCount && (
                                    <Body2 text={errors.studentCount} style={{ color: 'var(--error-color, #ef4444)', fontSize: '0.8em' }} />
                                )}
                            </FlexCol>

                            <FlexCol gap={0.5}>
                                <Body2 text="Email *" />
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    placeholder="your.email@school.edu"
                                    className={`educator-form-input ${showErrors && errors.email ? 'error' : ''}`}
                                />
                                {showErrors && errors.email && (
                                    <Body2 text={errors.email} style={{ color: 'var(--error-color, #ef4444)', fontSize: '0.8em' }} />
                                )}
                            </FlexCol>
                        </FlexCol>

                        <FlexRow justify="center" gap={1} mTop={0.5}>
                            <BCPrimaryButton 
                                text="Next" 
                                onClick={handleNext}
                                iconRight={ICONS.RIGHT_ARROW}
                            />
                        </FlexRow>
                    </FlexCol>
                )}

                {currentStep === 3 && (
                    <FlexCol gap={1.5} widthPercent={100}>
                        <FlexRow justify="space-between" align="center" widthPercent={100}>
                            <FlexRow gap={1} align="center">
                                <BCIcon 
                                    icon={ICONS.LEFT_ARROW} 
                                    onClick={() => setCurrentStep(2)}
                                    style={{ 
                                        cursor: 'pointer', 
                                        fontSize: '1.5em',
                                        color: 'var(--secondary-text-color)'
                                    }}
                                />
                                <Subtitle1 text={selectedAction === 'call' ? 'Schedule Your Call' : 'Email Inquiry'} />
                            </FlexRow>
                            <FlexRow gap={1} align="center">
                                <div 
                                    className={`progress-step ${currentStep >= 1 ? 'active' : ''}`}
                                    style={{
                                        width: '1.8em',
                                        height: '1.8em',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: currentStep >= 1 ? 'var(--primary-blue)' : 'var(--border-default)',
                                        color: currentStep >= 1 ? 'white' : 'var(--secondary-text-color)',
                                        fontWeight: 'bold',
                                        fontSize: '0.8em'
                                    }}
                                >
                                    1
                                </div>
                                <div 
                                    className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}
                                    style={{
                                        width: '1.8em',
                                        height: '1.8em',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: currentStep >= 2 ? 'var(--primary-blue)' : 'var(--border-default)',
                                        color: currentStep >= 2 ? 'white' : 'var(--secondary-text-color)',
                                        fontWeight: 'bold',
                                        fontSize: '0.8em'
                                    }}
                                >
                                    2
                                </div>
                                <div 
                                    className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}
                                    style={{
                                        width: '1.8em',
                                        height: '1.8em',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: currentStep >= 3 ? 'var(--primary-blue)' : 'var(--border-default)',
                                        color: currentStep >= 3 ? 'white' : 'var(--secondary-text-color)',
                                        fontWeight: 'bold',
                                        fontSize: '0.8em'
                                    }}
                                >
                                    3
                                </div>
                            </FlexRow>
                        </FlexRow>

                        {selectedAction === 'email' && (
                            <FlexCol gap={1} widthPercent={100}>
                                <Body2 text="Message *" />
                                <textarea
                                    value={formData.emailInquiry || ''}
                                    onChange={(e) => handleInputChange('emailInquiry', e.target.value)}
                                    placeholder="Please describe your organization's needs, how many students you have, what you're looking for, etc."
                                    className={`educator-form-input ${showErrors && errors.emailInquiry ? 'error' : ''}`}
                                    style={{
                                        minHeight: '8em',
                                        resize: 'vertical',
                                        fontFamily: 'inherit'
                                    }}
                                />
                                {showErrors && errors.emailInquiry && (
                                    <Body2 text={errors.emailInquiry} style={{ color: 'var(--error-color, #ef4444)', fontSize: '0.8em' }} />
                                )}
                            </FlexCol>
                        )}

                        {selectedAction === 'call' && (
                            <FlexCol gap={1} widthPercent={100} >
                                <iframe
                                    src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ12rPyutvHdNv85hMVjQHmd0u8XpyfljA4ZPAx2neJkK12AtcIdeJBL0Zud6kqjfwVNFN4a-Mhd?gv=true"
                                    style={{border: 0}}
                                    width="100%"
                                    height="400"
                                    frameBorder="0"
                                    title="Schedule a call with Beyond Code"
                                />
                                {submitError && (
                                    <Body2 
                                        text={submitError} 
                                        style={{ 
                                            color: 'var(--error-color, #ef4444)', 
                                            fontSize: '0.8em',
                                            textAlign: 'center'
                                        }} 
                                    />
                                )}
                                <FlexRow justify="center" mTop={0.5}>
                                    <BCSecondaryButton
                                        text={isSubmitting ? "Sending..." : "I scheduled a Call"}
                                        onClick={async () => {
                                            setIsSubmitting(true);
                                            setSubmitError(null);
                                            const emailSent = await sendEmail('call');
                                            if (emailSent) {
                                                setCurrentStep(4);
                                            } else {
                                                setSubmitError('Failed to send confirmation. Please try again or contact us directly.');
                                            }
                                            setIsSubmitting(false);
                                        }}
                                        iconRight={isSubmitting ? ICONS.CLOCK : ICONS.CHECKMARK_CIRCLE}
                                        style={{ width: '100%' }}
                                        disabled={isSubmitting}
                                    />
                                </FlexRow>
                            </FlexCol>
                        )}

                        {selectedAction === 'email' && (
                            <FlexCol gap={1} widthPercent={100}>
                                {submitError && (
                                    <Body2 
                                        text={submitError} 
                                        style={{ 
                                            color: 'var(--error-color, #ef4444)', 
                                            fontSize: '0.8em',
                                            textAlign: 'center'
                                        }} 
                                    />
                                )}
                                <FlexRow justify="center" mTop={0.5}>
                                    <BCPrimaryButton
                                        text={isSubmitting ? "Sending..." : "Submit"}
                                        onClick={handleSubmit}
                                        iconRight={isSubmitting ? ICONS.CLOCK : ICONS.CHECKMARK_CIRCLE}
                                        style={{ width: '100%' }}
                                        disabled={isSubmitting}
                                    />
                                </FlexRow>
                            </FlexCol>
                        )}
                    </FlexCol>
                )}

                {/* Step 4: Confirmation Screen */}
                {currentStep === 4 && (
                    <FlexCol gap={2} widthPercent={100} justify="center" align="center" mTop={1}>

                        {/* Confirmation Text */}
                        <FlexCol gap={1} align="center">
                            <FlexRow justify={"center"} align={"center"} gap={1}>
                                <BCIcon
                                    icon={ICONS.CHECKMARK_CIRCLE}
                                    style={{
                                        fontSize: '2em',
                                        color: 'var(--primary-blue)'
                                    }}
                                />
                                <BCHeader3 text="Completed" style={{ textAlign: 'center', fontSize: '1.8rem', fontWeight: 600 }} />
                            </FlexRow>
                            <Subtitle1 text="We will be in touch soon!" style={{ textAlign: 'center', fontSize: '.9rem', fontWeight: 400 }} />
                        </FlexCol>

                        {/* Done Button */}
                            <BCPrimaryButton
                                text="Done"
                                onClick={onClose}
                                style={{ width: '100%' }}
                            />
                    </FlexCol>
                )}

                {currentStep === 2 && (
                    <FlexCol gap={1.5} widthPercent={100}>
                        {/* Combined Step Counter and Subtitle */}
                        <FlexRow justify="space-between" align="center" widthPercent={100}>
                            <FlexRow gap={1} align="center">
                                <BCIcon 
                                    icon={ICONS.LEFT_ARROW} 
                                    onClick={() => setCurrentStep(1)}
                                    style={{ 
                                        cursor: 'pointer', 
                                        fontSize: '1.5em',
                                        color: 'var(--secondary-text-color)'
                                    }}
                                />
                                <Subtitle1 text="How would you like to proceed?" />
                            </FlexRow>
                            <FlexRow gap={1} align="center">
                                <div 
                                    className={`progress-step ${currentStep >= 1 ? 'active' : ''}`}
                                    style={{
                                        width: '1.8em',
                                        height: '1.8em',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: currentStep >= 1 ? 'var(--primary-blue)' : 'var(--border-default)',
                                        color: currentStep >= 1 ? 'white' : 'var(--secondary-text-color)',
                                        fontWeight: 'bold',
                                        fontSize: '0.8em'
                                    }}
                                >
                                    1
                                </div>
                                <div 
                                    className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}
                                    style={{
                                        width: '1.8em',
                                        height: '1.8em',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: currentStep >= 2 ? 'var(--primary-blue)' : 'var(--border-default)',
                                        color: currentStep >= 2 ? 'white' : 'var(--secondary-text-color)',
                                        fontWeight: 'bold',
                                        fontSize: '0.8em'
                                    }}
                                >
                                    2
                                </div>
                                <div 
                                    className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}
                                    style={{
                                        width: '1.8em',
                                        height: '1.8em',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: currentStep >= 3 ? 'var(--primary-blue)' : 'var(--border-default)',
                                        color: currentStep >= 3 ? 'white' : 'var(--secondary-text-color)',
                                        fontWeight: 'bold',
                                        fontSize: '0.8em'
                                    }}
                                >
                                    3
                                </div>
                            </FlexRow>
                        </FlexRow>
                        
                        {/* Action Selection */}
                        <FlexCol gap={1}>
                                <FlexCol 
                                    className="action-option"
                                    onClick={() => {
                                        setSelectedAction('call');
                                        setCurrentStep(3);
                                    }}
                                    style={{
                                        padding: '1.2em',
                                        border: '2px solid',
                                        borderColor: 'var(--border-default)',
                                        borderRadius: '1em',
                                        background: 'white',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <FlexRow gap={1} align="center">
                                        <BCIcon 
                                            icon={ICONS.PHONE} 
                                            style={{ 
                                                fontSize: '1.5em', 
                                                color: 'var(--secondary-text-color)' 
                                            }} 
                                        />
                                        <FlexCol gap={0.5}>
                                            <BCHeader3 text="Schedule a Call" />
                                            <Body2 text="Let's discuss how Beyond Code can help your students" />
                                        </FlexCol>
                                    </FlexRow>
                                </FlexCol>

                                <FlexCol 
                                    className="action-option"
                                    onClick={() => {
                                        setSelectedAction('email');
                                        setCurrentStep(3);
                                    }}
                                    style={{
                                        padding: '1.2em',
                                        border: '2px solid',
                                        borderColor: 'var(--border-default)',
                                        borderRadius: '1em',
                                        background: 'white',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <FlexRow gap={1} align="center">
                                        <BCIcon 
                                            icon={ICONS.EMAIL} 
                                            style={{ 
                                                fontSize: '1.5em', 
                                                color: 'var(--secondary-text-color)' 
                                            }} 
                                        />
                                        <FlexCol gap={0.5}>
                                            <BCHeader3 text="Email Inquiry" />
                                            <Body2 text="Send us a detailed message about your needs" />
                                        </FlexCol>
                                    </FlexRow>
                                </FlexCol>
                            </FlexCol>

                    </FlexCol>
                )}
            </FlexCol>
        </div>
    );
};
